import { Pencil, X } from 'lucide-react'
import { useEffect } from 'react'

import {
  formatearMedioPago,
  formatearFechaHora,
  obtenerEstadoPago,
} from '../../utils/gastos'
import {
  formatearFechaIngreso,
  formatearMontoIngreso,
  formatearTipoDocumentoIngreso,
} from '../../utils/ingresos'
import { formatearRut } from '../../utils/rut'

function Seccion({ titulo, children, ultima = false }) {
  return (
    <div
      className={
        ultima ? '' : 'mb-8 border-b border-slate-100 pb-8'
      }
    >
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
        {titulo}
      </h3>

      {children}
    </div>
  )
}

function Dato({ etiqueta, valor }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {etiqueta}
      </p>

      <p className="mt-1 text-sm font-medium text-slate-800">
        {valor}
      </p>
    </div>
  )
}

function Monto({ etiqueta, valor }) {
  return (
    <div className="rounded-xl bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {etiqueta}
      </p>

      <p className="mt-1 text-lg font-bold text-slate-900">
        {formatearMontoIngreso(valor)}
      </p>
    </div>
  )
}

function DetalleIngresoModal({
  abierto,
  ingreso,
  onCerrar,
  onEditar,
}) {
  useEffect(() => {
    if (!abierto) return

    const alPresionarTecla = (event) => {
      if (event.key === 'Escape') {
        onCerrar?.()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [abierto, onCerrar])

  if (!abierto || !ingreso) return null

  const estado = obtenerEstadoPago(ingreso.estado_pago)

  const alHacerClicFuera = (event) => {
    if (event.target === event.currentTarget) {
      onCerrar?.()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={alHacerClicFuera}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="detalle-ingreso-titulo"
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-6">
          <div>
            <h2
              id="detalle-ingreso-titulo"
              className="text-xl font-bold text-slate-900"
            >
              Detalle del ingreso
            </h2>

            <span
              className={`mt-2 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${estado.clase}`}
            >
              {estado.etiqueta}
            </span>
          </div>

          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 sm:p-8">
          <Seccion titulo="Información general">
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <Dato
                etiqueta="Fecha del ingreso"
                valor={formatearFechaIngreso(
                  ingreso.fecha_ingreso
                )}
              />

              <Dato
                etiqueta="Cliente"
                valor={
                  ingreso.cliente?.razon_social || 'Sin cliente'
                }
              />

              <Dato
                etiqueta="RUT del cliente"
                valor={
                  ingreso.cliente?.rut
                    ? formatearRut(ingreso.cliente.rut)
                    : '—'
                }
              />

              <Dato
                etiqueta="Tipo de documento"
                valor={formatearTipoDocumentoIngreso(
                  ingreso.tipo_documento
                )}
              />

              <Dato
                etiqueta="Número de documento"
                valor={ingreso.numero_documento || '—'}
              />

              <div className="sm:col-span-2">
                <Dato
                  etiqueta="Descripción"
                  valor={ingreso.descripcion || '—'}
                />
              </div>
            </div>
          </Seccion>

          <Seccion titulo="Montos">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
              <Monto etiqueta="Neto" valor={ingreso.monto_neto} />
              <Monto etiqueta="IVA" valor={ingreso.monto_iva} />
              <Monto
                etiqueta="Exento"
                valor={ingreso.monto_exento}
              />
              <Monto
                etiqueta="Otros impuestos"
                valor={ingreso.otros_impuestos}
              />

              <div className="rounded-xl bg-emerald-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Total
                </p>

                <p className="mt-1 text-lg font-bold text-emerald-900">
                  {formatearMontoIngreso(ingreso.monto_total)}
                </p>
              </div>
            </div>
          </Seccion>

          <Seccion titulo="Pago">
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <Dato
                etiqueta="Estado de pago"
                valor={estado.etiqueta}
              />

              <Dato
                etiqueta="Medio de pago"
                valor={formatearMedioPago(ingreso.medio_pago)}
              />

              <Dato
                etiqueta="Fecha de vencimiento"
                valor={formatearFechaIngreso(
                  ingreso.fecha_vencimiento
                )}
              />

              <Dato
                etiqueta="Fecha de pago"
                valor={formatearFechaIngreso(ingreso.fecha_pago)}
              />
            </div>
          </Seccion>

          <Seccion titulo="Información adicional" ultima>
            <Dato
              etiqueta="Observaciones"
              valor={ingreso.observaciones || 'Sin observaciones'}
            />

            <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <Dato
                etiqueta="Fecha de creación"
                valor={formatearFechaHora(ingreso.creado_en)}
              />

              <Dato
                etiqueta="Última modificación"
                valor={formatearFechaHora(
                  ingreso.actualizado_en
                )}
              />
            </div>
          </Seccion>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-200 p-6 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCerrar}
            className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 sm:w-auto"
          >
            Cerrar
          </button>

          {onEditar && (
            <button
              type="button"
              onClick={() => onEditar(ingreso.id)}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 sm:w-auto"
            >
              <Pencil size={18} />
              Editar ingreso
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default DetalleIngresoModal
